import { Alert, Button, List, ListItem, ListItemText, ListSubheader, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React from "react";
import Modal, { CommonModalProps } from "..";
import {
  CheckUpdateMetadataFieldsResponse,
  ConditionError,
} from "../../../Core/Api/Types/check-update-metadata-fields-response";
import { DataGrid } from "../../Table/styled";
import { useTranslation } from "react-i18next";
import DataTable from "../../Table/table";

export type ModalCheckUpdateMetadataFieldsElementProps = {
  checkUpdateResponse: CheckUpdateMetadataFieldsResponse;
  onOverrideCheck: () => void;
};

export type ModalCheckUpdateMetadataFieldsProps = CommonModalProps<ModalCheckUpdateMetadataFieldsElementProps>;

const ModalCheckUpdateMetadataFields = React.forwardRef<any, ModalCheckUpdateMetadataFieldsProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...modalProps } = props;
  const { checkUpdateResponse, onOverrideCheck } = modalProps;

  const handleOverrideClick = () => {
    onOverrideCheck();
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  const warnsColumns: GridColDef[] = [
    {
      field: "fieldId",
      headerName: t("table:headerName.UpperCaseFieldId"),
      flex: 1,
    },
    {
      field: "issue",
      headerName: t("common:issue"),
      flex: 1,
    },
    {
      field: "type",
      headerName: t("common:type"),
      flex: 1,
    },
  ];

  return (
    <Modal
      title={t("modal:title.metadataUpdateCheckFailed")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("button:changeMetas")}</Button>
          <Button color="warning" variant="outlined" onClick={handleOverrideClick}>
            {t("button:overrideCheckAndUpdateMetas")}
          </Button>
        </Stack>
      }
    >
      {checkUpdateResponse.success == false && checkUpdateResponse.message && (
        <Alert sx={{ mb: 2 }} severity="warning">
          {checkUpdateResponse.message}
        </Alert>
      )}

      {checkUpdateResponse.warns && checkUpdateResponse.warns.length > 0 && (
        <DataTable
          noControls
          rows={checkUpdateResponse.warns}
          columns={warnsColumns}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
        />
      )}

      {checkUpdateResponse.checkErrors && checkUpdateResponse.checkErrors.length > 0 && (
        <List
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            listStyleType: "disc",
            pl: 2,
            "& .MuiListItem-root": {
              display: "list-item",
              listStyleType: "inherit",
            },
          }}
          subheader={
            <ListSubheader component="div">
              {t("modal:modalCheckUpdateMetadataFields.body.checkErrorList.subHeader")}
            </ListSubheader>
          }
        >
          {checkUpdateResponse.checkErrors.map((error, index) => (
            <ListItem key={index} sx={{ display: "list-item" }}>
              <ListItemText primary={error} />
            </ListItem>
          ))}
        </List>
      )}

      {checkUpdateResponse.conditionErrors && checkUpdateResponse.conditionErrors.length > 0 && (
        <ConditionErrorList errors={checkUpdateResponse.conditionErrors} />
      )}
    </Modal>
  );
});

const ConditionErrorList = ({ errors }: { errors: ConditionError[] }) => {
  return (
    <List sx={{ pl: 1 }} dense>
      {errors.map((error, index) => (
        <React.Fragment key={index}>
          {error.message && (
            <ListItem
              sx={{
                display: "list-item",
                listStyleType: "disc",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                wordWrap: "break-word",
              }}
            >
              <ListItemText
                primary={error.message}
                sx={{ overflowWrap: "break-word", wordBreak: "break-word", whiteSpace: "normal" }}
              />
            </ListItem>
          )}
          {error.subConditionsErrors && error.subConditionsErrors.length > 0 && (
            <ConditionErrorList errors={error.subConditionsErrors} />
          )}
        </React.Fragment>
      ))}
    </List>
  );
};

export default ModalCheckUpdateMetadataFields;
